import { Component, OnInit } from '@angular/core';
import { BusinessRulesService } from './business-rules.service';
import { ActivatedRoute } from '@angular/router';
import { NotifierService } from 'src/app/services/notifier.service';

interface Range {
  race_length_min: number;
  race_length_max: number | null;
  min?: number;
  max?: number;
  segments?: { [key: string]: [number, number] };
  labels?: { [key: string]: string };
  sections?: { [key: string]: [number, number] };
  label?: string;
}

interface BusinessRuleSection {
  ranges: Range[];
  NonStandardSegmentRanges?: {
    Segments: { [key: string]: string };
    ranges: Range[];
  };
}

interface BusinessRules {
  [key: string]: {
    average_speed?: BusinessRuleSection;
    top_speed_method1?: BusinessRuleSection;
    top_speed_method2?: BusinessRuleSection;
    stride_length?: BusinessRuleSection;
    stride_frequency?: BusinessRuleSection;
  };
}

@Component({
  selector: 'app-business-rules-configuration',
  templateUrl: './business-rules-configuration.component.html',
  styleUrls: ['./business-rules-configuration.component.css'],
})
export class BusinessRulesConfigurationComponent implements OnInit {
  config: any;
  businessRules: BusinessRules = {};
  currentType: 'harness' | 'thoroughbred' = 'harness';
  types: ['harness', 'thoroughbred'] = ['harness', 'thoroughbred'];
  currentSegmentType: 'standard' | 'non-standard' = 'standard';
  loading = {
    main: false,
    save: false,
  };

  // Track edited cells
  editedCells: { [key: string]: boolean } = {};

  // Distance ranges - will be populated from response
  distanceRanges: { min: number; max: number | null }[] = [];
  avgSpeedDistanceRanges: { min: number; max: number | null }[] = [];
  nonStandardDistanceRanges: { min: number; max: number | null }[] = [];
  nonStandardOtherMetricsRanges: { min: number; max: number | null }[] = [];

  // Non-standard segment configuration
  minSegmentLength = 200; // Default minimum segment length

  // Labels for average speed
  speedLabels = ['Lead time', 'Q1', 'Q2', 'Q3', 'Q4'];
  nonStandardSpeedLabels = ['Lead time', 'Q1', 'Q2', 'Q3', 'Q4'];

  constructor(
    private businessRulesService: BusinessRulesService,
    private activatedRoute: ActivatedRoute,
    private notifier: NotifierService
  ) {
    this.config = this.activatedRoute.snapshot.data['config'];
  }

  ngOnInit() {
    this.loadBusinessRules();
  }

  async loadBusinessRules() {
    try {
      this.loading.main = true;
      const result = await this.businessRulesService.getBusinessRules(
        this.config,
        this.currentType
      );
      if (result.status === '1' && result.response) {
        this.businessRules[this.currentType] = result.response;
        this.processData();
      }
    } catch (error) {
      this.notifier.alert(
        'Error',
        '',
        'Failed to load business rules',
        'error'
      );
    } finally {
      this.loading.main = false;
    }
  }

  segments: any = [];
  processData() {
    this.segments = [];
    const rules = this.businessRules[this.currentType];
    if (!rules?.average_speed) return;

    // Process standard ranges for average speed
    if (rules.average_speed.ranges) {
      this.avgSpeedDistanceRanges = rules.average_speed.ranges.map((range) => ({
        min: range.race_length_min,
        max: range.race_length_max,
      }));
    }

    // Process standard ranges for other metrics
    if (rules.stride_length?.ranges) {
      this.distanceRanges = rules.stride_length.ranges.map((range) => ({
        min: range.race_length_min,
        max: range.race_length_max,
      }));
    }

    // Process non-standard ranges
    if (rules.average_speed.NonStandardSegmentRanges?.ranges) {
      this.nonStandardDistanceRanges =
        rules.average_speed.NonStandardSegmentRanges.ranges.map((range) => ({
          min: range.race_length_min,
          max: range.race_length_max,
        }));

      if (
        rules.average_speed.NonStandardSegmentRanges.Segments &&
        rules.average_speed.NonStandardSegmentRanges.Segments['0']
      ) {
        this.minSegmentLength = parseInt(
          rules.average_speed.NonStandardSegmentRanges.Segments['0']
        );
      }
    }

    // Process non-standard ranges for other metrics
    if (rules.stride_length?.NonStandardSegmentRanges?.ranges) {
      this.nonStandardOtherMetricsRanges = rules.stride_length.NonStandardSegmentRanges.ranges.map((range) => ({
        min: range.race_length_min,
        max: range.race_length_max,
      }));
    }

    // Process labels and segments
    const processRanges = (ranges: Range[], isNonStandard: boolean) => {
      if (!ranges || ranges.length === 0) return;

      // Get labels from the range with the highest length or highest range
      const rangeWithLabels = ranges.reduce((prev, curr) => {
        const prevLength = (prev.race_length_max || 0) - prev.race_length_min;
        const currLength = (curr.race_length_max || 0) - curr.race_length_min;
        return currLength > prevLength ? curr : prev;
      });

      if (rangeWithLabels?.labels) {
        const labels = Object.keys(rangeWithLabels.labels).map(
          (key) => rangeWithLabels.labels![key]
        );

        if (isNonStandard) {
          this.nonStandardSpeedLabels = labels;
        } else {
          this.speedLabels = labels;
        }
      }

      ranges.forEach((range, index_) => {
        if (!range.segments) range.segments = {};

        if (range.sections) {
          Object.keys(range.sections).forEach((sectionKey, index) => {
            range.segments![index.toString()] = range.sections![sectionKey];
            if(this.currentType === 'thoroughbred' && index_ === (ranges.length - 1) && this.segments.indexOf(sectionKey) === -1) {
              this.segments.push(sectionKey);
            }
          });
        }

        if (range.segments) {
          Object.keys(range.segments).forEach((sectionKey, index) => {
            if(this.currentType === 'harness' && index_ === (ranges.length - 1) && this.segments.indexOf(sectionKey) === -1) {
              this.segments.push(sectionKey);
            }
          });
        }

        // Ensure all label indexes have corresponding segment values
        const targetLabels = isNonStandard
          ? this.nonStandardSpeedLabels
          : this.speedLabels;
        targetLabels.forEach((_, index) => {
          if (!range.segments![index.toString()]) {
            range.segments![index.toString()] = [0, 0];
          }
        });
      });
    };

    // Process standard and non-standard ranges separately
    if (rules.average_speed.ranges) {
      processRanges(rules.average_speed.ranges, false);
    }
    if (rules.average_speed.NonStandardSegmentRanges?.ranges) {
      processRanges(rules.average_speed.NonStandardSegmentRanges.ranges, true);
    }

    if(this.currentType == 'thoroughbred') {
      this.segments = this.segments.reverse();
    }
  }

  selectType(type: 'harness' | 'thoroughbred') {
    this.currentType = type;
    this.loadBusinessRules();
  }

  selectSegmentType(type: 'standard' | 'non-standard') {
    this.currentSegmentType = type;
  }

  getCurrentRanges() {
    if (this.currentSegmentType === 'non-standard') {
      return this.nonStandardDistanceRanges;
    }
    return this.avgSpeedDistanceRanges;
  }

  getOtherMetricsRanges() {
    if (this.currentSegmentType === 'non-standard') {
      return this.nonStandardOtherMetricsRanges;
    }
    return this.distanceRanges;
  }

  getCurrentLabels() {
    return this.currentSegmentType === 'standard'
      ? this.speedLabels
      : this.nonStandardSpeedLabels;
  }

  getCurrentSegmentLengths() {
    const maxLength =
      this.currentSegmentType === 'standard'
        ? this.distanceRanges.length
        : this.nonStandardDistanceRanges.length;
  }

  // Conversion methods
  private mpsToKmph(mps: number): number {
    return Number((mps * 3.6));
  }

  private kmphToMps(kmph: number): number {
    return Number((kmph / 3.6));
  }

  getSpeedValue(
    rangeIndex: number,
    labelIndex: number,
    isMin: boolean
  ): number {
    const rules = this.businessRules[this.currentType];
    if (!rules?.average_speed) return 0;

    const ranges =
      this.currentSegmentType === 'standard'
        ? rules.average_speed.ranges
        : rules.average_speed.NonStandardSegmentRanges?.ranges;

    if (!ranges?.[rangeIndex]) return 0;

    const range = ranges[rangeIndex];
    let values;

    if (this.currentType === 'thoroughbred' && range.sections) {
      // For thoroughbred, use the actual segment name from segments array
      const segmentName = this.segments[labelIndex];
      values = segmentName ? range.sections[segmentName] : null;
    } else {
      // For harness, use segment index
      const key = this.segments[labelIndex];
      values = range.segments?.[key];
    }

    return values ? Number(this.mpsToKmph(values[isMin ? 0 : 1]).toFixed(2)) : 0;
  }

  getMetricValue(rangeIndex: number, metric: string, isMin: boolean): number {
    const rules = this.businessRules[this.currentType];
    if (!rules || !rules[metric]) return 0;

    let range;
    if (this.currentSegmentType === 'standard') {
      if (!rules[metric].ranges?.[rangeIndex]) return 0;
      range = rules[metric].ranges[rangeIndex];
    } else {
      if (!rules[metric].NonStandardSegmentRanges?.ranges?.[rangeIndex]) return 0;
      range = rules[metric].NonStandardSegmentRanges.ranges[rangeIndex];
    }

    const value = isMin ? range.min || 0 : range.max || 0;

    // Convert speed values from m/s to km/h
    if (metric.includes('speed')) {
      return Number(this.mpsToKmph(value).toFixed(2));
    }
    return value;
  }

  onSpeedEdit(
    event: any,
    rangeIndex: number,
    labelIndex: number,
    isMin: boolean
  ) {
    const kmphValue = parseFloat(event.target.value);
    if (isNaN(kmphValue)) return;

    // Convert from km/h to m/s for storage
    const mpsValue = this.kmphToMps(kmphValue);

    const rules = this.businessRules[this.currentType];
    if (!rules?.average_speed) return;

    const ranges =
      this.currentSegmentType === 'standard'
        ? rules.average_speed.ranges
        : rules.average_speed.NonStandardSegmentRanges?.ranges;

    if (!ranges) return;

    const range = ranges[rangeIndex];
    let key = labelIndex.toString();

    if(this.currentType == 'harness') {
      if (!range.segments) range.segments = {};
      if (!range.segments[key]) range.segments[key] = [0, 0];
      key = this.segments[key];
      if(key == undefined || range.segments[key] == undefined) {
        range.segments[key] = [0, 0];
      }
      range.segments[key][isMin ? 0 : 1] = mpsValue;
    } else {
      if (!range.sections) range.sections = {};
      if (!range.sections[key]) range.sections[key] = [0, 0];
      key = this.segments[key];
      if(key == undefined || range.sections[key] == undefined) {
        range.sections[key] = [0, 0];
      }
      range.sections[key][isMin ? 0 : 1] = mpsValue;
    }

    this.editedCells[`speed_${rangeIndex}_${labelIndex}_${isMin}`] = true;
  }

  onMetricEdit(event: any, rangeIndex: number, metric: string, isMin: boolean) {
    const value = parseFloat(event.target.value);
    if (isNaN(value)) return;

    const rules = this.businessRules[this.currentType];
    if (!rules || !rules[metric]) return;

    let range;
    if (this.currentSegmentType === 'standard') {
      if (!rules[metric].ranges?.[rangeIndex]) return;
      range = rules[metric].ranges[rangeIndex];
    } else {
      if (!rules[metric].NonStandardSegmentRanges?.ranges?.[rangeIndex]) return;
      range = rules[metric].NonStandardSegmentRanges.ranges[rangeIndex];
    }

    // Convert km/h to m/s for speed values
    const convertedValue = metric.includes('speed')
      ? this.kmphToMps(value)
      : value;

    if (isMin) {
      range.min = convertedValue;
    } else {
      range.max = convertedValue;
    }

    this.editedCells[`${metric}_${rangeIndex}_${isMin}`] = true;
  }

  isEdited(key: string): boolean {
    return this.editedCells[key] || false;
  }

  isNonStandardSegment(segmentLength: number): boolean {
    return segmentLength < this.minSegmentLength;
  }

  getSegmentLength(raceLength: number, segmentIndex: number): number {
    // This is a placeholder - you'll need to implement the actual logic
    // to calculate segment length based on your race data
    return 402; // Default segment length for harness
  }

  saveChanges() {
    try {
      this.loading.save = true;

      const rules = this.businessRules[this.currentType];
      if (!rules.average_speed) return;

      // Update standard ranges for average_speed
      if (rules.average_speed.ranges) {
        rules.average_speed.ranges = rules.average_speed.ranges.map((range) => {
          const ranges = Object.values(range.segments).filter(
            (segment) => segment[0] !== 0
          );
          range.labels = {};
          this.speedLabels
            .filter((_, index) => index < ranges.length)
            .forEach((label, index) => {
              range.labels![index] = label;
            });
          return range;
        });
      }

      // Update non-standard ranges for average_speed
      if (rules.average_speed.NonStandardSegmentRanges) {
        rules.average_speed.NonStandardSegmentRanges.Segments = {
          '0': this.minSegmentLength.toString(),
        };
        rules.average_speed.NonStandardSegmentRanges.ranges =
          rules.average_speed.NonStandardSegmentRanges.ranges.map((range) => {
            const ranges = Object.values(range.segments).filter(
              (segment) => segment[0] !== 0
            );
            range.labels = {};
            // Use nonStandardSpeedLabels instead of speedLabels for non-standard segments
            this.nonStandardSpeedLabels
              .filter((_, index) => index < ranges.length)
              .forEach((label, index) => {
                range.labels![index] = label;
              });
            return range;
          });
      }

      // Ensure NonStandardSegmentRanges exists and is properly updated for all other rule sections
      const metrics = [
        'stride_length',
        'stride_frequency',
        'top_speed_method1',
        'top_speed_method2',
      ];
      metrics.forEach((metric) => {
        if (rules[metric]) {
          // Create or update NonStandardSegmentRanges
          if (!rules[metric].NonStandardSegmentRanges) {
            rules[metric].NonStandardSegmentRanges = {
              Segments: {
                '0': this.minSegmentLength.toString(),
              },
              ranges: []
            };
          }

          // Ensure the ranges array exists
          if (!rules[metric].NonStandardSegmentRanges.ranges) {
            rules[metric].NonStandardSegmentRanges.ranges = [];
          }

          // Update the minimum segment length
          rules[metric].NonStandardSegmentRanges.Segments = {
            '0': this.minSegmentLength.toString(),
          };

          // Ensure all ranges have proper labels
          rules[metric].NonStandardSegmentRanges.ranges =
            rules[metric].NonStandardSegmentRanges.ranges.map((range, index) => {
              range.label = `Section ${index + 1}`;
              return range;
            });
        }
      });

      // For top_speed_method1 and top_speed_method2, handle their values and labels
      if (rules.top_speed_method1 && rules.top_speed_method2) {
        // Process standard ranges
        if (rules.top_speed_method1.ranges && rules.top_speed_method2.ranges) {
          for (let i = 0; i < Math.min(rules.top_speed_method1.ranges.length, rules.top_speed_method2.ranges.length); i++) {
            const method1Range = rules.top_speed_method1.ranges[i];
            const method2Range = rules.top_speed_method2.ranges[i];
            method2Range.min = method1Range.min;
            method2Range.max = method1Range.max;
            method1Range.label = method2Range.label || `Section ${i + 1}`;
          }
        }

        // Process non-standard ranges
        if (rules.top_speed_method1.NonStandardSegmentRanges?.ranges &&
            rules.top_speed_method2.NonStandardSegmentRanges?.ranges) {
          const m1Ranges = rules.top_speed_method1.NonStandardSegmentRanges.ranges;
          const m2Ranges = rules.top_speed_method2.NonStandardSegmentRanges.ranges;

          for (let i = 0; i < Math.min(m1Ranges.length, m2Ranges.length); i++) {
            const method1Range = m1Ranges[i];
            const method2Range = m2Ranges[i];
            method2Range.min = method1Range.min;
            method2Range.max = method1Range.max;
            method1Range.label = method2Range.label || `Section ${i + 1}`;
          }
        }
      }

      // Save changes
      this.businessRulesService
        .saveBusinessRules(
          this.config,
          this.currentType,
          this.businessRules[this.currentType]
        )
        .then((result) => {
          if (result.status === '1') {
            this.notifier.alert(
              'Success',
              '',
              'Changes saved successfully',
              'success'
            );
            this.editedCells = {};
            // Reload the data to ensure everything is in sync
            this.loadBusinessRules();
          } else {
            this.notifier.alert(
              'Error',
              '',
              result.error_message || 'Failed to save changes',
              'error'
            );
          }
        });
    } catch (error) {
      this.notifier.alert('Error', '', 'Failed to save changes', 'error');
    } finally {
      this.loading.save = false;
    }
  }

  // Handle label edits
  onLabelEdit(event: any, index: number) {
    const value = event.target.value.trim();

    // Update the correct label array based on segment type
    const targetLabels = this.currentSegmentType === 'standard' ? this.speedLabels : this.nonStandardSpeedLabels;

    if (value && value !== targetLabels[index]) {
      if (this.currentSegmentType === 'standard') {
        this.speedLabels[index] = value;
      } else {
        this.nonStandardSpeedLabels[index] = value;
      }
      this.editedCells[`label_${this.currentSegmentType}_${index}`] = true;
    }
  }

  // Calculate segment name based on the nearest 100m value
  // Replace the existing calculateSegmentName method with this simplified version
  calculateSegmentName(minValue: number, rangeIndex: number = -1): string {
    if (rangeIndex < 0) {
      return 'Unnamed Segment';
    }

    const rules = this.businessRules[this.currentType];
    if (!rules?.average_speed) return 'Unnamed Segment';

    const ranges =
      this.currentSegmentType === 'standard' ||
      this.currentType === 'thoroughbred'
        ? rules.average_speed.ranges
        : rules.average_speed.NonStandardSegmentRanges?.ranges;

    if (!ranges || !ranges[rangeIndex]) return 'Unnamed Segment';

    // Simply return the label property directly
    return ranges[rangeIndex].label || 'Unnamed Segment';
  }

  onRangeEdit(event: any, rangeIndex: number, field: 'min' | 'max', isAvgSpeed: boolean = false) {
    const value = event.target.value.trim();
    const numValue = value === '' ? null : parseFloat(value);

    if (field === 'min' && (isNaN(numValue!) || numValue === null)) return;
    if (field === 'max' && isNaN(numValue!) && value !== '') return;

    const rules = this.businessRules[this.currentType];
    if (!rules?.average_speed) return;

    if (isAvgSpeed) {
      // Update average speed ranges
      const ranges =
        this.currentSegmentType === 'standard'
          ? rules.average_speed.ranges
          : rules.average_speed.NonStandardSegmentRanges?.ranges;

      if (!ranges) return;

      const range = ranges[rangeIndex];

      if (field === 'min') {
        range.race_length_min = numValue!;
      } else {
        range.race_length_max = value === '' ? null : numValue;
      }

      // Update the local average speed ranges array
      if (this.currentSegmentType === 'standard') {
        this.avgSpeedDistanceRanges[rangeIndex] = {
          min: range.race_length_min,
          max: range.race_length_max,
        };
      } else {
        this.nonStandardDistanceRanges[rangeIndex] = {
          min: range.race_length_min,
          max: range.race_length_max,
        };
      }
    } else {
      // Update other metrics ranges
      const metrics = [
        'stride_length',
        'stride_frequency',
        'top_speed_method1',
        'top_speed_method2',
      ];

      metrics.forEach((metric) => {
        if (this.currentSegmentType === 'standard') {
          if (rules[metric]?.ranges?.[rangeIndex]) {
            const range = rules[metric].ranges[rangeIndex];
            if (field === 'min') {
              range.race_length_min = numValue!;
            } else {
              range.race_length_max = value === '' ? null : numValue;
            }
          }
        } else {
          if (rules[metric]?.NonStandardSegmentRanges?.ranges?.[rangeIndex]) {
            const range = rules[metric].NonStandardSegmentRanges.ranges[rangeIndex];
            if (field === 'min') {
              range.race_length_min = numValue!;
            } else {
              range.race_length_max = value === '' ? null : numValue;
            }
          }
        }
      });

      // Update the local distance ranges array
      if (this.currentSegmentType === 'standard') {
        this.distanceRanges[rangeIndex][field] = value === '' ? null : numValue;
      } else {
        this.nonStandardOtherMetricsRanges[rangeIndex][field] = value === '' ? null : numValue;
      }
    }

    this.editedCells[`range_${rangeIndex}_${field}`] = true;
  }

  addNewRange(isAvgSpeed: boolean = false) {
    const rules = this.businessRules[this.currentType];
    if (!rules?.average_speed) return;

    if (isAvgSpeed) {
      // For average speed ranges
      const ranges =
        this.currentSegmentType === 'standard' ||
        this.currentType === 'thoroughbred'
          ? rules.average_speed.ranges
          : rules.average_speed.NonStandardSegmentRanges?.ranges;

      if (ranges) {
        // Create a new range without a label (we'll use segment/section keys instead)
        const newRange: Range = {
          race_length_min:
            ranges.length > 0
              ? (ranges[ranges.length - 1].race_length_max || 0) + 100
              : 0,
          race_length_max: null,
        };

        if (this.currentType === 'harness') {
          // For harness, set default segment key as "1"
          newRange.segments = { '1': [0, 0] };
          // Optional: Set a default label
          newRange.labels = { '1': 'Segment 1' };
        } else {
          // For thoroughbred, set default section key as "200m"
          newRange.sections = { '200m': [0, 0] };
        }

        ranges.push(newRange);

        // Update local tracking for average speed
        if (this.currentSegmentType === 'standard') {
          this.avgSpeedDistanceRanges.push({
            min: newRange.race_length_min,
            max: newRange.race_length_max,
          });
        } else {
          this.nonStandardDistanceRanges.push({
            min: newRange.race_length_min,
            max: newRange.race_length_max,
          });
        }
      }
    } else {
      // For other metrics
      const metrics = ['stride_length', 'stride_frequency', 'top_speed_method1', 'top_speed_method2'];
      const newRange: Range = {
        race_length_min: this.currentSegmentType === 'standard'
          ? (this.distanceRanges.length > 0 ? (this.distanceRanges[this.distanceRanges.length - 1].max || 0) + 100 : 0)
          : (this.nonStandardOtherMetricsRanges.length > 0 ? (this.nonStandardOtherMetricsRanges[this.nonStandardOtherMetricsRanges.length - 1].max || 0) + 100 : 0),
        race_length_max: null,
        min: 0,
        max: 0
      };

      metrics.forEach(metric => {
        if (this.currentSegmentType === 'standard') {
          if (rules[metric]?.ranges) {
            rules[metric].ranges.push({ ...newRange });
          }
        } else {
          // Ensure NonStandardSegmentRanges exists
          if (!rules[metric].NonStandardSegmentRanges) {
            rules[metric].NonStandardSegmentRanges = {
              Segments: {
                '0': this.minSegmentLength.toString()
              },
              ranges: []
            };
          }
          rules[metric].NonStandardSegmentRanges.ranges.push({ ...newRange });
        }
      });

      // Update local tracking for other metrics
      if (this.currentSegmentType === 'standard') {
        this.distanceRanges.push({
          min: newRange.race_length_min,
          max: newRange.race_length_max
        });
      } else {
        this.nonStandardOtherMetricsRanges.push({
          min: newRange.race_length_min,
          max: newRange.race_length_max
        });
      }
    }

    // Mark as added
    this.editedCells['newRange'] = true;
    if(isAvgSpeed) {
      this.addNewRange(false);
    }
  }

  removeRange(rangeIndex: number, isAvgSpeed: boolean = false) {
    const rules = this.businessRules[this.currentType];
    if (!rules?.average_speed) return;

    if (isAvgSpeed) {
      // Remove from average speed ranges
      if (this.currentSegmentType === 'standard') {
        rules.average_speed.ranges.splice(rangeIndex, 1);
        this.avgSpeedDistanceRanges.splice(rangeIndex, 1);
      } else {
        if (rules.average_speed.NonStandardSegmentRanges?.ranges) {
          rules.average_speed.NonStandardSegmentRanges.ranges.splice(rangeIndex, 1);
          this.nonStandardDistanceRanges.splice(rangeIndex, 1);
        }
      }
    } else {
      // Remove from other metrics
      const metrics = [
        'stride_length',
        'stride_frequency',
        'top_speed_method1',
        'top_speed_method2',
      ];

      metrics.forEach((metric) => {
        if (this.currentSegmentType === 'standard') {
          if (rules[metric]?.ranges) {
            rules[metric].ranges.splice(rangeIndex, 1);
          }
        } else {
          if (rules[metric]?.NonStandardSegmentRanges?.ranges) {
            rules[metric].NonStandardSegmentRanges.ranges.splice(rangeIndex, 1);
          }
        }
      });

      // Update the local ranges array based on segment type
      if (this.currentSegmentType === 'standard') {
        this.distanceRanges.splice(rangeIndex, 1);
      } else {
        this.nonStandardOtherMetricsRanges.splice(rangeIndex, 1);
      }
    }

    this.editedCells[`range_removed_${Date.now()}`] = true;
  }

  // Add a new label (column) to the average speed table
  addNewLabel() {
    // Generate a default name for the new label
    const newLabelNum = this.speedLabels.length + 1;
    const newLabel = `Column ${newLabelNum}`;
    this.speedLabels.push(newLabel);

    // Add this new column to all existing ranges
    const rules = this.businessRules[this.currentType];
    if (rules?.average_speed?.ranges) {
      const newIndex = this.speedLabels.length - 1;
      rules.average_speed.ranges.forEach((range) => {
        if (!range.segments) range.segments = {};
        range.segments[newIndex.toString()] = [0, 0];
      });
    }

    this.editedCells[`label_added_${Date.now()}`] = true;
  }

  // Remove a specific label (column) from the average speed table
  removeLabel(labelIndex: number) {
    if (this.speedLabels.length <= 1) {
      this.notifier.alert(
        'Info',
        '',
        'You must keep at least one column',
        'info'
      );
      return;
    }

    // Remove the label from the speedLabels array
    this.speedLabels.splice(labelIndex, 1);

    // Reorganize the segment indices in all ranges
    const rules = this.businessRules[this.currentType];
    if (rules?.average_speed?.ranges) {
      rules.average_speed.ranges.forEach((range) => {
        if (!range.segments) return;

        // Create a new segments object with reindexed keys
        const newSegments: { [key: string]: [number, number] } = {};

        this.speedLabels.forEach((_, index) => {
          // If index < labelIndex, keep the same data
          // If index >= labelIndex, take data from index + 1
          const sourceIndex =
            index >= labelIndex ? (index + 1).toString() : index.toString();

          // Only copy if the source data exists
          if (range.segments![sourceIndex]) {
            newSegments[index.toString()] = range.segments![sourceIndex];
          } else {
            newSegments[index.toString()] = [0, 0]; // Default values
          }
        });

        range.segments = newSegments;
      });
    }

    this.editedCells[`label_removed_${Date.now()}`] = true;
  }

  onSegmentNameEdit(event: any, rangeIndex: number) {
    const value = event.target.value.trim();
    if (!value) return;

    const rules = this.businessRules[this.currentType];
    if (!rules?.average_speed) return;

    const ranges =
      this.currentSegmentType === 'standard' ||
      this.currentType === 'thoroughbred'
        ? rules.average_speed.ranges
        : rules.average_speed.NonStandardSegmentRanges?.ranges;

    if (!ranges || !ranges[rangeIndex]) return;

    // Simply set the label directly
    ranges[rangeIndex].label = value;

    this.editedCells[`segmentName_${rangeIndex}`] = true;
  }
}
